import React, { KeyboardEvent, useEffect, useMemo, useState, useRef } from "react";
import { Button, Form, Icon, Label, Input, Popup } from "semantic-ui-react";

// serializer
import CentralLabOrderSimplifiedSerializer from "issara-sdk/types/CentralLabOrderSimplifiedSerializer_apps_LAB";
import SpecimenContainerSerializer from "issara-sdk/types/SpecimenContainerSerializer_apps_LAB";
import CentralLabOrderItemActionLogSerializer from "issara-sdk/types/CentralLabOrderItemActionLogSerializer_apps_LAB";

// UX
import CardSpecimenCollector from "./CardSpecimenCollector";
import CardSpecimenCollectorListUX from "./CardSpecimenCollectorListUX";

// Common
import ModInfo from "react-lib/apps/common/ModInfo";

// Utils
import { formatDatetime } from "react-lib/utils/dateUtils";
import { useIntl } from "react-intl";
import { LabOrderQueueType, SpecimenCollectorMasterDataType } from "react-lib/apps/HISV3/LAB/LabInterface";

type CardLabPanelProps = {
  runSequence: any;
  setProp: any;
  onEvent: any;
  selectedDivision: Record<string, any>;
  selectedEncounter: Record<string, any>;
  errorMessage: Record<string, any>;
  successMessage: Record<string, any>;
  buttonLoadCheck: Record<string, any>;
  django: Record<string, any>;
  labOrderQueue: LabOrderQueueType;
  selectedLabOrderWorking: CentralLabOrderSimplifiedSerializer;
  selectedContainer?: SpecimenContainerSerializer | null;
  specimenLogs?: CentralLabOrderItemActionLogSerializer[];
  specimenCollectorMasterData?: SpecimenCollectorMasterDataType;
  languageUX: string;
};

const CardLabPanel = (props: CardLabPanelProps) => {
  const intl = useIntl();
  const [searchText, setSearchText] = useState<string>("");
  const [openModInfo, setOpenModInfo] = useState<boolean>(false);

  const cardSpecimenCollectorRef = useRef<any>();

  useEffect(() => {
    // props.onEvent({ message: "ScanBarcodeStickerSpecimen", params: {} });
    props.onEvent({
      message: "GetLabOrderQueue",
      params: {
        action: "search",
        hn: props.selectedEncounter?.hn,
      },
    });
  }, []);

  const labOrderQueue = useMemo(() => {
    return (props?.labOrderQueue?.labOrderQueueList || [])
      .sort((a: any, b: any) => (a.highest_urgency <= b.highest_urgency ? 1 : -1))
      .map((item: any) => ({
        ...item,
        labId: item.full_access_number,
        arrive_time: formatDatetime(item.arrive_time_iso, true),
        status_name: (
          <div>
            {item.status_name}
            {item.order_item_have_rejected && <Label color="red">REJECT</Label>}
          </div>
        ),
        doctor_name: (
          <div
            style={{
              display: "flex",
              ...(item.highest_urgency === "STAT" && { color: "red" }),
            }}
          >
            <div style={{ width: "80%" }}>{item.order_by_name}</div>
            <div style={{ width: "20%" }}>
              {item.student && item.need_approve ? (
                <img src="/static/images/drugOrder/need_approve.png" />
              ) : (
                item.student &&
                !item.need_approve && (
                  <Popup
                    content={
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            color: "#898383",
                            fontSize: "12px",
                          }}
                        >
                          ตรวจสอบโดย
                        </div>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          {item.approved_by_name}
                        </div>
                      </div>
                    }
                    on="hover"
                    pinned
                    position="bottom center"
                    trigger={<img src="/static/images/drugOrder/approve_check_out_line.png" />}
                  />
                )
              )}
            </div>
          </div>
        ),
      }));
  }, [props?.labOrderQueue?.labOrderQueueList]);

  const handleSearchBarcode = (text: string) => {
    props.onEvent({
      message: "ScanBarcodeStickerSpecimen",
      params: {
        action: "scan",
        searchText: text,
        specimenList: cardSpecimenCollectorRef.current?.specimenList || [],
        callback: (status: "SUCCESS" | "ERROR") => {
          if (status === "SUCCESS") {
            setSearchText("");
          } else {
            setSearchText(searchText.trim());
            setOpenModInfo(true);
          }
        },
      },
    });
  };

  const handleCloseModInfo = () => {
    setOpenModInfo(false);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const value = (e.target as any).value || "";

      handleSearchBarcode(value);
      setSearchText(value);
    }
  };

  const handleGetRowProps = (state: any, rowInfo: any) => {
    return {
      style: {
        backgroundColor:
          rowInfo?.original?.id && rowInfo?.original?.id === props.selectedLabOrderWorking?.id
            ? "#cccccc"
            : "white",
        color: rowInfo?.original?.highest_urgency === "STAT" ? "red" : "",
      },
      onClick: () => {
        props.setProp("selectedLabOrderWorking", rowInfo?.original?.id);
        props.onEvent({
          message: "LabOrderArrive",
          params: {
            action: "getDataLabOrderArrive",
            labOrderId: rowInfo?.original?.id,
            labOrder: rowInfo?.original,
          },
        });
      },
    };
  };

  return (
    <div id="CardLabPanel" style={{ height: "94vh" }}>
      <div style={{ padding: "10px", minWidth: "820px" }}>
        <div
          style={{
            display: "flex",
            marginBottom: "10px",
            alignItems: "center",
            fontSize: "1.2rem",
            fontWeight: "bold",
          }}
        >
          <div style={{ marginRight: "10px" }}>
            {intl.formatMessage({ id: "รายการ Lab Order" })}
          </div>
          <div style={{ flex: 1 }}></div>

          <div style={{ marginRight: "10px" }}>
            {intl.formatMessage({ id: "สแกนรหัส Specimen (tube) ID" })}
          </div>
          <Input
            value={searchText}
            placeholder="Tube Sticker ID"
            size="mini"
            onKeyPress={handleKeyPress}
            onChange={(e: any, v: any) => {
              setSearchText(v.value);
            }}
          ></Input>
          <Button
            icon="search"
            color="blue"
            onClick={() => handleSearchBarcode(searchText)}
          ></Button>
        </div>

        <div style={{ margin: "10px 0px 20px" }}>
          <CardSpecimenCollectorListUX
            labOrderQueue={labOrderQueue}
            rowProps={handleGetRowProps}
            languageUX={props.languageUX}
          />
        </div>
      </div>

      <div style={{ padding: "10px 10px 30px" }}>
        <CardSpecimenCollector
          ref={cardSpecimenCollectorRef}
          // function
          setProp={props.setProp}
          onEvent={props.onEvent}
          // common data
          errorMessage={props.errorMessage}
          successMessage={props.successMessage}
          buttonLoadCheck={props.buttonLoadCheck}
          django={props.django}
          // data
          selectedLabOrderWorking={props.selectedLabOrderWorking}
          selectedContainer={props.selectedContainer}
          specimenLogs={props.specimenLogs}
          specimenCollectorMasterData={props.specimenCollectorMasterData}
          selectedDivision={props.selectedDivision}
          selectedEncounter={props.selectedEncounter}
          // config
          languageUX={props.languageUX}
        />
      </div>

      <ModInfo
        open={openModInfo}
        titleColor={"red"}
        titleName={intl.formatMessage({ id: "ไม่พบรหัสสิ่งส่งตรวจ (Specimen ID)" })}
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
      >
        {/* <ErrorMessage error={openModError.error} /> */}
      </ModInfo>

      {/* <div style={{ padding: "10px" }}>
        <CardLabResult
          setProp={props.setProp}
          onEvent={props.onEvent}
          labOrderItems={props.labOrderItems || []}
          // To be deprecated
          selectedLabOrderItem={props.selectedLabOrderItem}
          selectedLabOrderItemChild={props.selectedLabOrderItemChild}
          errorMessage={props.errorMessage}
          successMessage={props.successMessage}
        />
      </div> */}
    </div>
  );
};

CardLabPanel.displayName = "CardLabPanel";

export default CardLabPanel;